// SigninOutcomeBanner
//
// Dominant top-of-card callout for the `_signin_outcome` object the detection
// pipeline stamps onto Defender/AADIP pass-through alerts. The load-bearing
// triage fact (handoff §4): the credential is CONFIRMED COMPROMISED when the
// password was valid (or the sign-in outright succeeded) — and only Conditional
// Access / MFA stood between the attacker and the account. That fact outranks
// everything else on the card, so this renders FIRST in the expanded row.
//
// Rendered by both AlertsPage and TestRunResultsPage (CLAUDE.md rule #13 parity).
// On the Alerts page (showRecheckButton) a `pending` stamp — or a pass-through
// alert with a sign-in but no stamp yet — gets a gated "Re-check sign-in outcome"
// button that fills the outcome in on demand (and the backend persists it).
//
// For valid_password_blocked we surface WHICH CA policy saved them with per-chip
// residual-risk tiers (§5a): MFA = lower risk, Location/IP = higher (attacker can
// pivot IP/region and the same valid password may then succeed).

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ArrowPathIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import Badge from '../ui/Badge';
import { postRecheckSigninOutcome } from '../../api/client';
import { normalizeIsoUtc, resolveTz } from '../../utils/timeFormat';
import {
  effectiveSigninOutcome, extractRecheckInputs, signinOutcomeView,
  isPendingOutcome, blockedByList, recheckGate,
} from '../../utils/signinOutcome';

function fmtTs(ts, tzIana) {
  if (!ts) return null;
  try {
    // resolveTz maps "Local" (empty iana) → browser zone (empty timeZone throws);
    // normalizeIsoUtc fixes zone-less / sub-ms parsing. Legacy Z shape only for an
    // actual UTC selection. See feedback-local-tz-empty-iana.
    const zone = resolveTz(tzIana);
    const d = new Date(normalizeIsoUtc(ts));
    if (zone === 'UTC') {
      return d.toISOString().replace('T', ' ').replace(/\.\d{3}Z$/, 'Z');
    }
    return d.toLocaleString('en-US', {
      timeZone: zone, year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', hour12: false,
    });
  } catch { return String(ts); }
}

export default function SigninOutcomeBanner({ alert, rawEvent, tzIana, showRecheckButton = false, boxed = false, onResolved }) {
  const [resolved, setResolved] = useState(null);   // outcome from a re-check this session
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const [retryMsg, setRetryMsg] = useState(null);
  const [nowMs, setNowMs] = useState(Date.now());

  const inputs = useMemo(() => extractRecheckInputs(rawEvent), [rawEvent]);
  const baseOutcome = useMemo(() => effectiveSigninOutcome(alert, rawEvent), [alert, rawEvent]);
  const effective = resolved || baseOutcome;
  const view = signinOutcomeView(effective);

  const pending = !effective || isPendingOutcome(effective);
  const canRecheckTarget = !!inputs.requestId;          // pass-through alert with a sign-in to look up
  const showButton = showRecheckButton && canRecheckTarget && pending;
  const gate = recheckGate(inputs.signinTime, nowMs);

  // Keep the countdown fresh + auto-enable the button when the 30-min gate lifts.
  useEffect(() => {
    if (!showButton || gate.eligible || gate.beyondHorizon) return undefined;
    const id = setInterval(() => setNowMs(Date.now()), 30000);
    return () => clearInterval(id);
  }, [showButton, gate.eligible, gate.beyondHorizon]);

  const onRecheck = useCallback(async () => {
    if (!inputs.requestId || loading) return;
    setLoading(true); setErr(null); setRetryMsg(null);
    try {
      const res = await postRecheckSigninOutcome(alert.alertId, {
        tenantId: alert.tenantId, requestId: inputs.requestId, signinTime: inputs.signinTime,
      });
      if (res?.found && res.outcome) {
        setResolved({ ...res.outcome, _source: 'recheck' });
        // Lift the result to the parent so it lands in the in-memory alerts array.
        // The backend already persisted it server-side, but without this the row's
        // `alert` object stays at its stale page-load value — so collapsing and
        // re-expanding (which unmounts this banner) would revert the display to
        // "pending". No-op on pages that don't pass onResolved (e.g. test runs).
        onResolved?.(alert.alertId, res);
      } else if (res?.reason === 'beyond_horizon') {
        setRetryMsg('The 14-day sign-in mirror no longer retains this sign-in.');
      } else {
        setRetryMsg('Sign-in detail not yet mirrored — retry shortly.');
      }
    } catch (e) {
      setErr(e?.message || String(e));
    } finally {
      setLoading(false);
    }
  }, [alert?.alertId, alert?.tenantId, inputs.requestId, inputs.signinTime, loading, onResolved]);

  // Nothing to show and nothing to re-check → render nothing (handoff §2a).
  if (!view && !canRecheckTarget) return null;

  const container = boxed
    ? 'rounded border p-3 text-xs'
    : 'px-6 py-3 border-b text-xs';

  const toneBorderBg =
    view?.tone === 'critical' ? 'border-red-800/60 bg-red-950/30'
      : view?.tone === 'review' ? 'border-amber-700/50 bg-amber-950/20'
        : view?.tone === 'muted' ? 'border-gray-800/60 bg-gray-900/20'
          : 'border-sky-900/40 bg-sky-950/10';     // neutral / pending

  // ── Detail sub-line: error code · CA status · sign-in time ──
  const subParts = [];
  if (effective) {
    if (effective.error_code != null && effective.error_code !== '') subParts.push(`Error ${effective.error_code}`);
    if (effective.ca_status) subParts.push(`CA ${effective.ca_status}`);
    const t = fmtTs(effective.signin_event_time || inputs.signinTime, tzIana);
    if (t) subParts.push(`sign-in ${t}`);
  }

  const blocked = effective ? blockedByList(effective) : [];
  const showBlocked = (effective?.status === 'valid_password_blocked' || effective?.status === 'blocked_by_ca_password_unconfirmed') && blocked.length > 0;

  return (
    <div className={`${container} ${toneBorderBg}`}>
      {/* Headline */}
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-2 min-w-0">
          {(view?.compromised || view?.tone === 'review') && (
            <ExclamationTriangleIcon className={`w-5 h-5 flex-none mt-0.5 ${view.compromised ? 'text-red-400' : 'text-amber-400'}`} />
          )}
          <div className="min-w-0">
            <p className={
              view?.compromised
                ? 'text-red-200 font-bold text-sm leading-tight break-words'
                : view?.tone === 'review'
                  ? 'text-amber-200 font-semibold break-words'
                  : view?.tone === 'muted'
                    ? 'text-gray-300 font-medium break-words'
                    : 'text-sky-200 font-semibold break-words'
            }>
              {view ? view.label : 'Sign-in outcome pending'}
            </p>
            {subParts.length > 0 && (
              <p className="text-gray-400 text-[11px] mt-0.5 break-words">{subParts.join(' · ')}</p>
            )}
          </div>
        </div>

        {/* Re-check button (Alerts page only, pending case) */}
        {showButton && (
          <div className="flex-none text-right">
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); onRecheck(); }}
              disabled={loading || !gate.eligible}
              title={
                gate.beyondHorizon ? 'The sign-in is older than the 14-day mirror retention — re-check is unavailable.'
                  : gate.noTime ? 'No sign-in activity time on this alert.'
                    : !gate.eligible ? `The sign-in mirror needs ~30 min to catch up. Available in ${gate.waitMinutes} min.`
                      : 'Look the correlated sign-in up and resolve its outcome.'
              }
              className="text-[11px] inline-flex items-center gap-1 px-2 py-1 rounded border border-sky-500/40 bg-sky-950/30 text-sky-300 hover:bg-sky-900/40 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {loading ? <ArrowPathIcon className="w-3 h-3 animate-spin" /> : <ArrowPathIcon className="w-3 h-3" />}
              {loading ? 'Checking…' : 'Re-check sign-in outcome'}
            </button>
            {!gate.eligible && !gate.beyondHorizon && !gate.noTime && (
              <p className="text-[10px] text-gray-500 mt-1">Available in {gate.waitMinutes} min</p>
            )}
            {gate.beyondHorizon && (
              <p className="text-[10px] text-gray-500 mt-1">Beyond 14-day horizon</p>
            )}
          </div>
        )}
      </div>

      {/* Re-check feedback */}
      {err && (
        <p className="text-[11px] mt-2 px-2 py-1 rounded bg-red-950/40 border border-red-500/30 text-red-300">
          Re-check failed: {err}
        </p>
      )}
      {retryMsg && !err && (
        <p className="text-[11px] mt-2 text-amber-300">{retryMsg}</p>
      )}

      {/* Pending explainer (only when there's no resolved outcome yet) */}
      {pending && !err && !retryMsg && (
        <p className="text-[11px] mt-1 text-gray-400">
          {canRecheckTarget
            ? 'The correlated sign-in had not been ingested when this alert fired. Re-check resolves whether the password was valid.'
            : 'No correlated sign-in is attached to this alert.'}
        </p>
      )}

      {/* Token-replay caveat for the unconfirmed-password state */}
      {view?.tone === 'review' && (
        <p className="text-[11px] mt-1 text-amber-200/80">
          A replayed or stolen token can trigger this CA block — a password wasn’t necessarily entered. Review the sign-in before treating the credential as compromised.
        </p>
      )}

      {/* Which Conditional Access policies blocked the sign-in (plain list of names). */}
      {showBlocked && (
        <div className="mt-2">
          <p className="text-[10px] uppercase tracking-wide text-gray-500 mb-1">
            Blocked by CA Policies:
          </p>
          <div className="flex flex-wrap gap-1.5">
            {blocked.map((name, i) => (
              <Badge key={`${name}-${i}`} tone="neutral">{name}</Badge>
            ))}
          </div>
        </div>
      )}

      {effective?._source === 'recheck' && (
        <p className="text-[10px] text-gray-600 mt-1">
          Resolved by re-check{alert?.signinOutcomeCheckedBy ? ` · ${alert.signinOutcomeCheckedBy}` : ''}
          {alert?.signinOutcomeCheckedUtc ? ` · ${fmtTs(alert.signinOutcomeCheckedUtc, tzIana)}` : ''}
        </p>
      )}
    </div>
  );
}
